import { Vendor, Tax, CurrencyEnum, ExpenseSplit, Expense, SplitTypeEnum, ExpenseKindEnum, ItemSplit, InvoiceSplit, Invoice } from "@finway-group/shared/lib/models"
import moment from "moment"
import { TFunction } from "i18next"
import * as stringSimilarity from "string-similarity"
import { FormInstance } from "antd/lib/form"
import i18n from "Shared/locales/i18n"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { isNumber } from "./helpers"

export const fillFormWithOcrData = (
    formInstance: FormInstance,
    expense: Expense,
    ocrObject: any,
    vendors: Vendor[],
    taxes: Tax[],
    updateExpense: (data: any) => void,
    setPrefilledVendorData: (vendorData: any) => void,
    globalCurrency: CurrencyEnum,
    ocrItemizationEnabled: boolean,
    t: TFunction
) => {
    const formFields = formInstance.getFieldsValue()
    if (!formFields.paymentOption) formFields.paymentOption = expense.paymentOption
    if (!formFields.kind) formFields.kind = expense.kind
    if (!formFields.splitType) formFields.splitType = expense.splitType
    const updateData = buildOcrUpdateData(ocrObject, formFields, vendors, taxes, globalCurrency, ocrItemizationEnabled)
    if (ocrObject.vendorData) setPrefilledVendorData({ ...ocrObject.vendorData, description: t("info:filled_by_ocr") })
    updateExpense(updateData)
}

export const buildOcrUpdateData = (ocrObject: any, existingData: Expense, vendors: Vendor[], taxes: Tax[], globalCurrency: CurrencyEnum, ocrItemizationEnabled: boolean) => {
    const { invoiceData, vendorData, itemsData } = ocrObject

    const foundVendor = vendors.find((vendor) => stringSimilarity.compareTwoStrings(vendor.name.toLocaleLowerCase(), vendorData?.name?.toLocaleLowerCase() || "") > 0.6)
    const doItemsAlreadyExist = existingData.splits?.length > 1 && existingData.splitType === SplitTypeEnum.ITEM
    const isExpenseSplitted = existingData.splits?.length > 1 && existingData.splitType === SplitTypeEnum.SPLIT
    const zeroPercentTaxRate = taxes.find((tax: Tax) => tax.taxRate === 0)?._id

    const ocrItemSplits: ItemSplit[] = (itemsData || []).map((itemSplit: any) => ({
        name: itemSplit.name ?? "Item",
        netPrice: itemSplit.netAmount ?? 0,
        grossPrice: itemSplit.grossAmount ?? 0,
        taxRate: taxes.find((tax: Tax) => tax.taxRate === itemSplit.tax && !tax.deleted)?._id || zeroPercentTaxRate,
        taxPrice: itemSplit.taxPrice ?? 0,
    }))

    const shouldUpdateItems =
        ocrItemizationEnabled && ocrItemSplits?.length > 0 && !doItemsAlreadyExist && !isExpenseSplitted && existingData.kind === ExpenseKindEnum.ONE_TIME_EXPENSE

    const isAutoGeneratedCardRequest = existingData.isAutoGenerated && existingData.paymentOption === ExpensePaymentOptionEnum.SMART_CARD

    // Determine prices for the update object
    const priceUpdates: { totalNetPrice?: number; totalGrossPrice?: number; totalTaxPrice?: number } = {}

    // For autoGenerated card requests we disregard the OCR amount prices
    if (!isAutoGeneratedCardRequest) {
        // Net price determination
        if (isNumber(invoiceData?.netAmount) && invoiceData?.netAmount !== 0) priceUpdates.totalNetPrice = invoiceData.netAmount
        if (shouldUpdateItems) priceUpdates.totalNetPrice = ocrItemSplits.reduce((total: number, i) => total + i.netPrice, 0)
        // Gross price determination
        if (isNumber(invoiceData?.grossAmount) && invoiceData?.grossAmount !== 0) priceUpdates.totalGrossPrice = invoiceData.grossAmount
        // Tax price determination
        if (isNumber(invoiceData?.taxAmount) && invoiceData?.taxAmount !== 0) priceUpdates.totalTaxPrice = invoiceData.taxAmount
    }

    const getSplitsUpdate = () => {
        // If shouldUpdateItems, assign item splits to the update object.
        if (shouldUpdateItems && !isAutoGeneratedCardRequest) return { splits: ocrItemSplits, splitType: SplitTypeEnum.ITEM }

        // If shouldnt update item, do nothing
        if (existingData.splitType === SplitTypeEnum.ITEM) {
            return {}
        }

        //Else construct the normal invoice splits for the update object.
        return {
            splits: existingData.splits?.map((s: InvoiceSplit) => ({
                ...s,
                netPrice: (priceUpdates.totalNetPrice ?? existingData.totalNetPrice ?? 0) * (s.percentage / 100),
                taxPrice: (priceUpdates.totalTaxPrice ?? existingData.totalTaxPrice ?? 0) * (s.percentage / 100),
                grossPrice: (priceUpdates.totalGrossPrice ?? existingData.totalGrossPrice ?? 0) * (s.percentage / 100),
            })),
        }
    }

    const shouldEditCurrency = existingData.currency === globalCurrency && existingData.paymentOption !== ExpensePaymentOptionEnum.SMART_CARD

    // We init the purchasedDate with today's date. If the user has changed the date, we shouldn't override it.
    const shouldOverridePurchasedDate = moment(existingData.datePurchased).isSame(moment(), "day")

    return {
        ...(invoiceData?.currency && shouldEditCurrency ? { currency: invoiceData.currency } : {}),
        ...(invoiceData?.number && !existingData.invoiceNumber ? { invoiceNumber: invoiceData.number } : {}),
        ...(invoiceData?.date && shouldOverridePurchasedDate ? { datePurchased: moment(invoiceData.date, "yyyy-MM-DD") } : {}),
        ...(invoiceData?.date && !existingData.invoiceDate ? { invoiceDate: moment(invoiceData.date, "yyyy-MM-DD") } : {}),
        ...(invoiceData?.dueOn && !existingData.invoiceDueDate ? { invoiceDueDate: moment(invoiceData.dueOn, "yyyy-MM-DD") } : {}),
        ...priceUpdates,
        ...getSplitsUpdate(),
        ...(isNumber(invoiceData?.tax)
            ? {
                  taxRate: taxes.find((tax: Tax) => tax.taxRate === invoiceData.tax && !tax.deleted)?._id ?? zeroPercentTaxRate,
              }
            : {}),
        ...(foundVendor && !existingData.vendor ? { vendor: foundVendor.id } : {}),
        ...(vendorData?.name && !existingData.name ? { name: vendorData.name + " " + i18n.t("label:subscription") } : {}),
    }
}
